"use client";

import { useEffect, useRef, useState } from "react";
import { FileText, ImageOff, Plus, Video, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { resizeImageFiles } from "@/lib/images/resize-client";
import {
  VIDEO_ACCEPTED_TYPES,
  VIDEO_MAX_UPLOAD_BYTES_HARD,
  isVideoMimeType,
} from "@/lib/video/constants";

// Match the chat-panel attachment constraints so a file accepted on the
// home prompt is also accepted in the in-project chat. Keep these in
// sync with components/chat/chat-panel.tsx.
const ACCEPTED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/json",
  ...VIDEO_ACCEPTED_TYPES,
];
const MAX_FILE_SIZE = 5 * 1024 * 1024;
// Per-video cap, identical to the server's (every plan shares the same 1 GB
// limit), so an oversize video is rejected right here at pick time and can
// never get through only to fail later when the message is sent.
const MAX_VIDEO_SIZE = VIDEO_MAX_UPLOAD_BYTES_HARD;
const MAX_FILES = 5;

function isImageType(type: string) {
  return type.startsWith("image/");
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function validateTypesAndCount(
  incoming: File[],
  existing: File[],
): { accepted: File[]; error?: string } {
  const totalAllowed = MAX_FILES - existing.length;
  if (totalAllowed <= 0) {
    return { accepted: [], error: `Maximum ${MAX_FILES} files per message` };
  }
  const accepted: File[] = [];
  for (const file of incoming.slice(0, totalAllowed)) {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return {
        accepted: [],
        error: `"${file.name}" is not a supported file type`,
      };
    }
    accepted.push(file);
  }
  // Over the cap, keep what fits and SAY what was dropped. Rejecting the whole
  // batch because of a later file threw away picks that were perfectly fine.
  const dropped = incoming.length - accepted.length;
  if (dropped > 0) {
    return {
      accepted,
      error: `Added ${accepted.length} of ${incoming.length} — maximum ${MAX_FILES} files per message`,
    };
  }
  return { accepted };
}

function checkSizes(files: File[]): string | undefined {
  for (const file of files) {
    const isVideo = isVideoMimeType(file.type);
    const cap = isVideo ? MAX_VIDEO_SIZE : MAX_FILE_SIZE;
    if (file.size > cap) {
      return isVideo
        ? `"${file.name}" is ${formatFileSize(file.size)} — the maximum video size is ${Math.round(
            MAX_VIDEO_SIZE / (1024 * 1024 * 1024),
          )} GB. Try compressing or trimming it.`
        : `"${file.name}" exceeds 5MB limit`;
    }
  }
  return undefined;
}

export type PromptAttachmentsProps = {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  /**
   * Switch attachments OFF on a surface, honestly. Sets `disabled`,
   * `aria-disabled` AND the tooltip from this one string, so the affordance and
   * the reality cannot drift apart.
   *
   * This replaces a `nonFunctional` prop that dropped only the click handler and
   * left the button at full opacity, hoverable, and still announcing itself as
   * "Add attachment" with a tooltip naming the file types it accepted. It read
   * as broken because there was no way to tell it apart from broken — QA filed
   * it as a High-severity bug (docs/prompt-attach-button-inert-on-home-and-projects.md).
   * A control that is off must LOOK off, and must say why.
   */
  disabledReason?: string;
  buttonClassName?: string;
  /**
   * The sticky home-page bar shares `files` with the hero card but has no
   * room for the 48px preview strip — it renders a count badge instead and
   * lets the hero card own the previews.
   */
  showPreviews?: boolean;
};

/**
 * Compact attach-button + preview strip used on the home hero and the
 * `/projects` new-project panel. Mirrors the chat-panel attachment UX
 * (same accepted types + file-size limits) so files picked pre-create
 * upload to the same /api/projects/[id]/attachments endpoint once the
 * project exists.
 */
export function PromptAttachments({
  files,
  onChange,
  disabled = false,
  disabledReason,
  buttonClassName,
  showPreviews = true,
}: PromptAttachmentsProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  // Images whose <img> thumbnail failed to decode. Tracked by File
  // reference so we render a labelled fallback card instead of the
  // browser's broken-image glyph + raw alt filename.
  const [thumbFailed, setThumbFailed] = useState<Set<File>>(new Set());
  function markThumbFailed(file: File) {
    setThumbFailed((prev) => {
      if (prev.has(file)) return prev;
      const next = new Set(prev);
      next.add(file);
      return next;
    });
  }

  // Cache object URLs so the same File renders the same preview between
  // renders (revoke on unmount + when a file is removed).
  const previewUrlsRef = useRef<Map<File, string>>(new Map());

  function getPreviewUrl(file: File): string {
    const cache = previewUrlsRef.current;
    if (!cache.has(file)) cache.set(file, URL.createObjectURL(file));
    return cache.get(file)!;
  }

  useEffect(() => {
    const cache = previewUrlsRef.current;
    return () => {
      cache.forEach((url) => URL.revokeObjectURL(url));
      cache.clear();
    };
  }, []);

  async function addFiles(incoming: File[]) {
    setError(null);
    const { accepted, error: typeError } = validateTypesAndCount(
      incoming,
      files,
    );
    // A partial accept reports what it dropped AND keeps what fitted, so the
    // notice is set before the early return rather than instead of the add.
    if (typeError) setError(typeError);
    if (accepted.length === 0) return;

    // Downscale oversized IMAGES BEFORE the 5 MB check so a 12 MB phone
    // photo gets shrunk to a few hundred KB instead of being rejected.
    // Aspect ratio preserved, no crop, no upscale — see resize-client.ts.
    // Videos are NOT canvas-resizable — pass them through untouched; they
    // upload to the video host as-is and the server enforces the size cap.
    const images = accepted.filter((f) => !isVideoMimeType(f.type));
    const videos = accepted.filter((f) => isVideoMimeType(f.type));
    const processed = [...(await resizeImageFiles(images)), ...videos];
    const sizeError = checkSizes(processed);
    if (sizeError) {
      setError(sizeError);
      return;
    }
    onChange([...files, ...processed]);
  }

  function removeFile(index: number) {
    const removed = files[index];
    if (removed) {
      const cache = previewUrlsRef.current;
      const url = cache.get(removed);
      if (url) {
        URL.revokeObjectURL(url);
        cache.delete(removed);
      }
    }
    onChange(files.filter((_, i) => i !== index));
  }

  // The single source of truth for "this button does nothing right now", and
  // for why. Everything the user perceives — the disabled attribute, the a11y
  // state, the tooltip — is derived from these two, so a future switch-off
  // cannot produce a live-looking dead control again.
  const atCap = files.length >= MAX_FILES;
  const offReason =
    disabledReason ?? (atCap ? `Maximum ${MAX_FILES} files` : undefined);
  const isOff = disabled || offReason !== undefined;

  return (
    <>
      {showPreviews && files.length > 0 && (
        <div className="flex items-center gap-2 px-3 pb-2 pt-1 overflow-x-auto">
          {/* eslint-disable-next-line react-hooks/refs -- getPreviewUrl lazily caches object URLs in a ref Map keyed by File; creating/reading them during render is intentional and the URLs are revoked on unmount + removal. */}
          {files.map((file, i) => {
            const showThumb = isImageType(file.type) && !thumbFailed.has(file);
            return (
              <div
                key={`${file.name}-${i}`}
                title={`${file.name} · ${formatFileSize(file.size)}`}
                className="relative shrink-0 group"
              >
                {showThumb ? (
                  /* eslint-disable-next-line @next/next/no-img-element */
                  <img
                    src={getPreviewUrl(file)}
                    alt={file.name}
                    onError={() => markThumbFailed(file)}
                    className="h-12 w-12 rounded-lg object-cover ring-1 ring-black/[0.06] dark:ring-white/[0.06]"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-12 w-12 flex-col items-center justify-center rounded-lg bg-zinc-100 ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/[0.06]">
                    {isImageType(file.type) ? (
                      <ImageOff className="h-4 w-4 text-zinc-400" />
                    ) : isVideoMimeType(file.type) ? (
                      <Video className="h-4 w-4 text-zinc-400" />
                    ) : (
                      <FileText className="h-4 w-4 text-zinc-400" />
                    )}
                    <span className="mt-0.5 max-w-[44px] truncate px-0.5 text-[8px] font-medium uppercase text-zinc-400">
                      {file.name.split(".").pop()}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  aria-label={`Remove ${file.name}`}
                  className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-zinc-800 text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100 dark:bg-zinc-600"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
                {showThumb && (
                  <span className="absolute bottom-0.5 left-0.5 right-0.5 truncate rounded-b-md bg-black/50 px-1 text-center text-[7px] text-white">
                    {formatFileSize(file.size)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_TYPES.join(",")}
        className="hidden"
        onChange={(e) => {
          const selected = Array.from(e.target.files || []);
          if (selected.length > 0) void addFiles(selected);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        aria-label="Add attachment"
        // One expression drives all three, so the button can never look
        // clickable while refusing clicks — the reported bug. At the cap it is
        // genuinely disabled rather than opening a picker whose every pick is
        // then rejected.
        disabled={isOff}
        aria-disabled={isOff}
        onClick={() => inputRef.current?.click()}
        title={offReason ?? "Attach images, PDFs, CSVs, or text files"}
        className={cn(
          "inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-white text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 dark:bg-background",
          buttonClassName,
        )}
      >
        <Plus className="h-4 w-4" strokeWidth={2.2} />
      </button>
      {error && (
        <p
          role="alert"
          className="ml-2 text-xs text-rose-600 dark:text-rose-400"
        >
          {error}
        </p>
      )}
    </>
  );
}
